import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { useLocalStorage } from 'Hooks';
import { getUserInfo } from 'Utils';
import * as analytics from 'Utils/analytics';
import { getUserFavorites, deleteUserFavorite } from 'Services/library';
import EmptyLibrary from 'Components/profile/EmptyLibrary';
import LibraryList from 'Components/profile/LibraryList';
import Nav from 'Components/profile/Nav';
import Recommendations from 'Components/profile/Recommendations';
import Select from 'Components/Select';
const { EVENTS } = analytics;
const TABS = {
  SHOWS: 'SHOW',
  FILMS: 'FILM',
  EVENTS: 'STOP',
  CLIPS: 'CLIP',
  STORIES: 'STORY',
};
const TAB_KEYS = Object.keys(TABS);
const FETCH_COUNT = 20;

const Library = ({ country, dispatchToast, language, strings, uim }) => {
  const [currentTab, setCurrentTab] = useLocalStorage(
    'RBAccountLibraryTabKey',
    TAB_KEYS[0]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [itemMap, setItemMap] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const nodeRef = useRef(null);

  const reset = () => {
    setIsLoading(true);
    setHasError(false);
    setItemList([]);
    setItemMap({});
    setTotalCount(0);
    setOffset(0);
  };

  const getTabFavorites = async (cancelPreviousReq = false) => {
    const type = TABS[currentTab];
    try {
      const { userId, userToken } = await getUserInfo(uim);
      const {
        expectedResourceCount,
        expectedTotalCount,
        isCancel,
        resourceCount,
        resources,
      } = await getUserFavorites({
        cancelPreviousReq,
        limit: FETCH_COUNT,
        locale: `${language}-${country}`,
        offset,
        type,
        userId,
        userToken,
      });

      // Fetching user tab favorites can be cancelled by quickly switching tabs,
      // the below ensures the loading state remains active in that case
      if (isCancel) return;

      const realTotalCount =
        (totalCount || expectedTotalCount) -
        (expectedResourceCount - resourceCount);
      const realOffset = offset + FETCH_COUNT;
      resources.forEach((item) => {
        if (!itemList.includes(item.id)) itemList.push(item.id);
        itemMap[item.id] = { ...item, isSaved: true };
      });
      setIsLoading(false);
      setHasError(false);
      setItemList(itemList);
      setItemMap(itemMap);
      setTotalCount(realTotalCount);
      setOffset(realOffset);
    } catch ({ message }) {
      console.error(`Error fetching '${type}' favorited content: ${message}`);
      dispatchToast({
        message: strings.errorFetching.formatUimTranslation(
          translateTab(currentTab)
        ),
      });
      setIsLoading(false);
      setHasError(true);
    }
  };

  const deleteFavorite = async (contentId) => {
    analytics.event({ action: EVENTS.removeLibraryItem, label: contentId });
    const contentIdx = itemList.indexOf(contentId);
    setItemList((itemList) => itemList.filter((id) => id !== contentId));
    setTotalCount((totalCount) => totalCount - 1);
    try {
      const { userToken, userId } = await getUserInfo(uim);
      await deleteUserFavorite({
        userToken,
        userId,
        contentId,
      });
      setItemMap((itemMap) => {
        const { [contentId]: deleted, ...rest } = itemMap;
        return rest;
      });
    } catch ({ message }) {
      console.error(`Error deleting favorite '${contentId}': ${message}`);
      dispatchToast({
        message: strings.couldNotRemove,
      });
      setItemList((itemList) => {
        itemList.splice(contentIdx, 0, contentId);
        return itemList;
      });
      setTotalCount((totalCount) => totalCount + 1);
    }
  };

  const translateTab = (key) => strings[key.toLowerCase()];

  const changeTab = (tab) => {
    if (currentTab !== tab) {
      analytics.event({
        action: EVENTS.selectLibraryView,
        label: tab.toLowerCase(),
      });
      reset();
      setCurrentTab(tab);
    }
  };

  const renderTabFavorites = () => {
    if (!isLoading && !hasError && !totalCount) {
      const cta = ['EVENTS', 'CLIPS'].includes(currentTab)
        ? strings.browse
        : strings.discover;
      return (
        <CSSTransition
          appear
          in
          key="emptyLibrary"
          classNames="fade"
          nodeRef={nodeRef}
          timeout={500}
        >
          <div ref={nodeRef}>
            <EmptyLibrary
              cta={cta}
              type={currentTab}
              typeTranslation={translateTab(currentTab)}
              youAddToYourLibrary={strings.youAddToYourLibrary}
              youHaventAddedAny={strings.youHaventAddedAny}
            />
          </div>
        </CSSTransition>
      );
    }

    return (
      <LibraryList
        country={country}
        hasError={hasError}
        isLoading={isLoading}
        itemList={itemList}
        itemMap={itemMap}
        onDeleteListItem={deleteFavorite}
        onLoadMore={getTabFavorites}
        shouldLoadMore={itemList.length < totalCount}
        strings={strings}
        totalCount={totalCount}
        type={currentTab}
      />
    );
  };

  useEffect(() => {
    getTabFavorites(true);
  }, [currentTab]);

  return (
    <div className="library">
      <div className="sidebar-view__header desktop__header">
        <h1 className="sidebar-view__heading">{strings.library}</h1>
        {/* Desktop navigation */}
        <Nav
          activeTab={currentTab}
          onClick={changeTab}
          strings={strings}
          tabs={TAB_KEYS}
        />
      </div>

      <div className="sidebar-view__inner">
        {/* Mobile navigation */}
        <Select
          onSelect={changeTab}
          options={TAB_KEYS}
          translateOption={translateTab}
          value={currentTab}
        />
        {renderTabFavorites()}
        <Recommendations
          country={country}
          dispatchToast={dispatchToast}
          favoritesType={TABS[currentTab]}
          language={language}
          strings={strings}
          type={currentTab}
          uim={uim}
        />
      </div>
    </div>
  );
};

Library.propTypes = {
  country: PropTypes.string.isRequired,
  dispatchToast: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  uim: PropTypes.object.isRequired,
};

Library.defaultProps = {};

export default Library;
